import { useState, useEffect } from 'react'
import { useToast } from '../../context/ToastContext'

const ALL_DEPARTMENTS = [
  { id: 'kitchen', label: 'Kitchen', businessName: 'Desres K&C' },
  { id: 'bakery', label: 'Bakery', businessName: 'Desres K&C' },
  { id: 'tuckshop', label: 'Tuck Shop', businessName: 'Desres K&C' },
  { id: 'yawehs', label: 'Bakery', businessName: "Yahweh's Delight" },
]

export default function UsersTab() {
  const { addToast } = useToast()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)

  const fetchUsers = async () => {
    setLoading(true)
    setFetchError(null)
    try {
      const { restList } = await import('../../lib/firestoreRest')
      const list = await restList('users')
      list.sort((a, b) => {
        const aHasAccess = (a.allowedDepartments || []).length > 0 || a.isApproved
        const bHasAccess = (b.allowedDepartments || []).length > 0 || b.isApproved
        if (aHasAccess !== bHasAccess) return aHasAccess ? 1 : -1
        const getTime = (val) => {
          if (!val) return 0
          if (typeof val === 'string') return new Date(val).getTime()
          if (typeof val === 'number') return val
          if (val._seconds) return val._seconds * 1000
          if (val.seconds) return val.seconds * 1000
          return 0
        }
        return getTime(b.createdAt) - getTime(a.createdAt)
      })
      setUsers(list.map(u => ({ ...u, uid: u.id })))
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setFetchError(err.message || 'Failed to load users')
      addToast('Failed to load users — check connection', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  const updateUser = async (uid, updates) => {
    const { restSet } = await import('../../lib/firestoreRest')
    const { restGet } = await import('../../lib/firestoreRest')
    const current = await restGet(`users/${uid}`) || {}
    await restSet(`users/${uid}`, { ...current, ...updates })
  }

  const handleDeptToggle = async (uid, deptId, currentDepts) => {
    const allowed = currentDepts || []
    const updated = allowed.includes(deptId)
      ? allowed.filter(d => d !== deptId)
      : [...allowed, deptId]
    try {
      await updateUser(uid, { allowedDepartments: updated, isApproved: updated.length > 0 })
      addToast(
        updated.includes(deptId)
          ? `${ALL_DEPARTMENTS.find(d => d.id === deptId)?.label} access granted`
          : `${ALL_DEPARTMENTS.find(d => d.id === deptId)?.label} access revoked`,
        'success'
      )
      fetchUsers()
    } catch (err) {
      addToast('Failed to update access', 'error')
    }
  }

  const handleGrantAll = async (uid) => {
    try {
      const allIds = ALL_DEPARTMENTS.map(d => d.id)
      await updateUser(uid, { allowedDepartments: allIds, isApproved: true })
      addToast('All department access granted', 'success')
      fetchUsers()
    } catch (err) {
      addToast('Failed to update access', 'error')
    }
  }

  const handleRevokeAll = async (uid) => {
    try {
      await updateUser(uid, { allowedDepartments: [], isApproved: false })
      addToast('All access revoked', 'success')
      fetchUsers()
    } catch (err) {
      addToast('Failed to update access', 'error')
    }
  }

  const handleRoleChange = async (uid, newRole) => {
    try {
      await updateUser(uid, { role: newRole })
      addToast(`Role changed to ${newRole}`, 'success')
      fetchUsers()
    } catch (err) {
      addToast('Failed to update role', 'error')
    }
  }

  if (loading) {
    return <p className="text-gray-500 text-sm text-center py-8">Loading users...</p>
  }

  if (fetchError) {
    return (
      <div className="text-center py-12 space-y-3">
        <p className="text-red-400 text-sm">Failed to load users: {fetchError}</p>
        <button onClick={fetchUsers} className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded text-sm">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
        <button onClick={fetchUsers} className="text-xs text-gray-500 hover:text-white px-2 py-1 rounded bg-gray-800 hover:bg-gray-700">
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        {users.map(u => {
          const allowed = u.allowedDepartments || []
          const hasAccess = allowed.length > 0 || u.isApproved
          const regDate = u.createdAt?.toDate?.()
            ? u.createdAt.toDate().toLocaleDateString('en-ZA')
            : '—'
          return (
            <div key={u.uid} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                  <span className="font-medium text-white">{u.displayName || '—'}</span>
                  <span className="text-gray-500 text-sm ml-2">{u.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    hasAccess
                      ? 'bg-green-900 text-green-400'
                      : 'bg-amber-900 text-amber-400'
                  }`}>
                    {hasAccess ? `${allowed.length} dept${allowed.length !== 1 ? 's' : ''}` : 'No Access'}
                  </span>
                  <select
                    value={u.role}
                    onChange={e => handleRoleChange(u.uid, e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-xs text-gray-300"
                  >
                    <option value="user">User</option>
                    <option value="staff">Staff</option>
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                  <span className="text-xs text-gray-600">{regDate}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {(() => {
                  const businesses = {}
                  for (const dept of ALL_DEPARTMENTS) {
                    const biz = dept.businessName || 'Other'
                    if (!businesses[biz]) businesses[biz] = []
                    businesses[biz].push(dept)
                  }
                  return Object.entries(businesses).map(([bizName, depts]) => (
                    <div key={bizName} className="flex items-center gap-1 mr-2">
                      <span className="text-[10px] text-gray-600 mr-0.5">{bizName}:</span>
                      {depts.map(dept => {
                        const isGranted = allowed.includes(dept.id)
                        return (
                          <button
                            key={dept.id}
                            onClick={() => handleDeptToggle(u.uid, dept.id, allowed)}
                            className={`text-xs px-2.5 py-1 rounded font-medium border transition-colors ${
                              isGranted
                                ? 'bg-brand-900 border-brand-700 text-brand-300 hover:bg-brand-800'
                                : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-400'
                            }`}
                          >
                            {isGranted ? '✓ ' : ''}{dept.label}
                          </button>
                        )
                      })}
                    </div>
                  ))
                })()}
                <div className="ml-auto flex gap-1">
                  <button
                    onClick={() => handleGrantAll(u.uid)}
                    className="text-[11px] px-2 py-1 rounded bg-green-900/50 hover:bg-green-900 text-green-400 transition-colors"
                  >
                    Grant All
                  </button>
                  <button
                    onClick={() => handleRevokeAll(u.uid)}
                    className="text-[11px] px-2 py-1 rounded bg-red-900/50 hover:bg-red-900 text-red-400 transition-colors"
                  >
                    Revoke All
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
